import React from 'react'
import { Link } from 'react-router-dom'
import { FileText, ArrowLeft, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../../components/layout/PublicFooter'
import { PublicNavigation } from '../../components/layout/PublicNavigation'
import { Button } from '../../components/ui/Button'

export const TroubleshootingPage: React.FC = () => {
  const issues = [
    {
      question: 'I can\'t upload my document',
      solution: 'Ensure your file is in a supported format (PDF, JPG, PNG) and under 10MB. Clear your browser cache and try again. If the problem persists, contact support.',
      icon: AlertCircle
    },
    {
      question: 'My exchange progress isn\'t updating',
      solution: 'Refresh the page and ensure you\'ve saved all required fields. Check that you\'ve completed all mandatory sections in the Exchange Hub.',
      icon: AlertCircle
    },
    {
      question: 'I missed a deadline notification',
      solution: 'Check your notification settings in your account profile. Ensure email notifications are enabled. Review your exchange dashboard for all upcoming deadlines.',
      icon: AlertCircle
    },
    {
      question: 'Neela isn\'t responding',
      solution: 'Refresh the chat interface. Ensure you have a stable internet connection. Try rephrasing your question. If issues continue, contact support.',
      icon: AlertCircle
    },
    {
      question: 'I can\'t access the Exchange Hub',
      solution: 'Ensure you\'re logged in to your account. Check that your account is fully verified. Clear your browser cookies and try logging in again.',
      icon: AlertCircle
    },
    {
      question: 'My document verification failed',
      solution: 'Review the rejection reason provided. Ensure documents are clear, complete, and match the information in your exchange. Re-upload with corrected documents.',
      icon: AlertCircle
    }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/documentation" className="inline-flex items-center text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Documentation
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <FileText className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Troubleshooting
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Common issues and solutions to help you resolve problems quickly.
            </p>
          </div>
        </div>
      </section>

      {/* Issues Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {issues.map((issue, index) => {
                const Icon = issue.icon
                return (
                  <div
                    key={index}
                    className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
                          {issue.question}
                        </h3>
                        <div className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-[#69adba] mt-0.5 flex-shrink-0" />
                          <p className="text-slate-600 dark:text-slate-400">
                            {issue.solution}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Still Need Help?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Our support team is available to assist you with any questions or issues.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/support">
              <Button className="bg-white text-[#69adba] hover:bg-slate-50">
                Contact Support
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                View FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

